import PublicListingsCard from 'components/public-listing-card/public-listing-card';
import { IconButton, Loader, Text } from 'components/ui';
import { useApiReady } from 'hooks/use-api-ready';
import { useMarketplacePublicListing } from 'hooks/use-marketplace-public-listing';
import {
  ListingRead,
  ListingTypeEnum,
  PaginatedListingReadList,
  States,
} from 'lib/api/generated';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { PATHS } from 'settings/config';
import styled from 'styled-components';
import { breakpoints } from 'styles/breakpoints';
import { globalTheme } from 'styles/global-theme';

type Props = {
  state?: States | undefined;
  product?: 'CT' | 'GT' | 'SH';
  type?: ListingTypeEnum;
};

const CarousalWrapper = styled.div`
  display: flex;
  gap: ${globalTheme.space[1]};
  overflow: auto;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const FeaturedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${globalTheme.space[2]};
  margin-top: ${globalTheme.space[2]};
`;

const Flex = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const FlexContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const CIconButton = styled(IconButton)`
  cursor: pointer;
  @media screen and (max-width: ${breakpoints.medium}) {
    display: none;
  }
`;

const FeaturedListings = ({ state, product, type }: Props) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const ref = useRef<HTMLDivElement | null>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const router = useRouter();
  const apiReady = useApiReady();
  const { data: session, status: sessionStatus } = useSession();
  const sessionResolved = sessionStatus !== 'loading';

  const isQueryEnabled =
    sessionResolved && (apiReady || sessionStatus === 'unauthenticated');

  const { data, isLoading: queryLoading } = useMarketplacePublicListing({
    type,
    product,
    state,
    apiKey: apiReady,
    pageSize: 10,
    enabled: isQueryEnabled,
    aa_source: 'portal.listing',
  });

  const listingsLoading = queryLoading || !isQueryEnabled;

  const listingData = useMemo(() => {
    if (!data) return [];
    return data.pages.flatMap((listing: PaginatedListingReadList) =>
      listing.results.filter((d) => d.id !== router.query.id)
    );
  }, [data, router.query.id]);

  const updateScrollButtons = () => {
    if (!ref.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = ref.current;

    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  useEffect(() => {
    updateScrollButtons();

    const el = ref.current;
    if (!el) return;

    el.addEventListener('scroll', updateScrollButtons);
    window.addEventListener('resize', updateScrollButtons);

    return () => {
      el.removeEventListener('scroll', updateScrollButtons);
      window.removeEventListener('resize', updateScrollButtons);
    };
  }, [listingData]);

  const onPrevClick = () => {
    if (!ref.current) return;
    ref.current.scrollBy({
      left: -ref.current.offsetWidth * 0.5,
      behavior: 'smooth',
    });
  };

  const onNextClick = () => {
    if (!ref.current) return;
    ref.current.scrollBy({
      left: ref.current.offsetWidth * 0.5,
      behavior: 'smooth',
    });
  };

  return (
    <FeaturedContainer ref={containerRef}>
      <FlexContainer>
        <Text as='h3' fontSize={4} fontWeight='bold'>
          Similar Listings
        </Text>

        {!listingsLoading && (
          <Flex>
            <CIconButton
              icon='ChevronBackIcon'
              size={6}
              onClick={onPrevClick}
              disabled={!canScrollLeft}
            />
            <CIconButton
              icon='ChevronNextIcon'
              size={6}
              onClick={onNextClick}
              disabled={!canScrollRight}
            />
          </Flex>
        )}
      </FlexContainer>

      {listingsLoading && <Loader />}

      {!listingsLoading && listingData && !listingData.length && (
        <Text>No Listings found!</Text>
      )}

      {!listingsLoading && (
        <CarousalWrapper ref={ref}>
          {listingData.map((data: ListingRead) => (
            <div style={{ minWidth: '340px' }} key={data.id}>
              <PublicListingsCard
                setMinWidth
                data={data}
                route={PATHS.marketplace.listings}
                isFeaturedListing
              />
            </div>
          ))}
        </CarousalWrapper>
      )}
    </FeaturedContainer>
  );
};

export default FeaturedListings;
